/**
 * CSV / JSON export for the Truth Layer Zoe question log.
 */
import { ANSWER_FEEDBACK_REASON_LABELS, type QuestionLogEntry } from '../types/zoeFeedback';

const CSV_COLUMNS: Array<{ header: string; value: (e: QuestionLogEntry) => string }> = [
  { header: 'Asked at', value: (e) => e.asked_at },
  { header: 'User', value: (e) => e.user_label ?? e.user_id },
  { header: 'User ID', value: (e) => e.user_id },
  { header: 'Question', value: (e) => e.question },
  { header: 'Interpreted as', value: (e) => e.effective_question ?? '' },
  { header: 'Headline', value: (e) => e.headline },
  { header: 'Answer', value: (e) => e.answer_text ?? '' },
  { header: 'Status', value: (e) => e.status },
  { header: 'Confidence', value: (e) => e.confidence ?? '' },
  { header: 'Conclusion', value: (e) => e.conclusion ?? '' },
  { header: 'Agent', value: (e) => e.agent_id ?? '' },
  { header: 'Error type', value: (e) => e.error?.kind ?? '' },
  { header: 'Error shown to user', value: (e) => e.error?.message ?? '' },
  { header: 'Error detail', value: (e) => e.error?.detail ?? '' },
  { header: 'Rating', value: (e) => e.feedback?.rating ?? '' },
  {
    header: 'Feedback reasons',
    value: (e) => (e.feedback?.reasons ?? []).map((r) => ANSWER_FEEDBACK_REASON_LABELS[r] ?? r).join('; '),
  },
  { header: 'Feedback comment', value: (e) => e.feedback?.comment ?? '' },
  { header: 'Feedback at', value: (e) => e.feedback?.submitted_at ?? '' },
  {
    header: 'Reviewer notes',
    value: (e) => e.notes.map((n) => `[${n.created_at} ${n.author_label ?? n.author_id}] ${n.text}`).join('\n'),
  },
  { header: 'Evidence digest', value: (e) => e.claim_digest.join('\n') },
  { header: 'Conversation ID', value: (e) => e.conversation_id },
  // With the conversation id, enough to put a thread back in order from the
  // flat file — and to see which questions were answers to a clarification.
  { header: 'Position in conversation', value: (e) => (typeof e.seq === 'number' ? String(e.seq) : '') },
  { header: 'Clarification reply', value: (e) => e.answered_clarification?.label ?? '' },
  { header: 'Run ID', value: (e) => e.run_id },
  // Wall-clock for answered turns; an errored question never became a turn, so
  // its own recorded duration stands in.
  {
    header: 'Duration (ms)',
    value: (e) => String(e.usage?.duration_ms ?? e.error?.duration_ms ?? ''),
  },
  // Prompt + completion + cached: what the run was billed for, matching the
  // Tokens column. The components follow so a reader can reconcile it.
  {
    header: 'Billed tokens',
    value: (e) =>
      e.usage
        ? String(e.usage.total_tokens + e.usage.cache_read_tokens + e.usage.cache_write_tokens)
        : '',
  },
  { header: 'Total tokens (prompt+completion)', value: (e) => (e.usage ? String(e.usage.total_tokens) : '') },
  { header: 'Prompt tokens', value: (e) => (e.usage ? String(e.usage.prompt_tokens) : '') },
  { header: 'Completion tokens', value: (e) => (e.usage ? String(e.usage.completion_tokens) : '') },
  { header: 'Cache read tokens', value: (e) => (e.usage ? String(e.usage.cache_read_tokens) : '') },
  { header: 'Cache write tokens', value: (e) => (e.usage ? String(e.usage.cache_write_tokens) : '') },
  { header: 'LLM latency (ms)', value: (e) => (e.usage ? String(e.usage.llm_latency_ms) : '') },
];

function csvCell(value: string): string {
  // Neutralise spreadsheet formula injection from free text (questions, comments).
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function questionLogToCsv(entries: QuestionLogEntry[]): string {
  const rows = [CSV_COLUMNS.map((c) => c.header), ...entries.map((e) => CSV_COLUMNS.map((c) => c.value(e)))];
  return rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
}

export function questionLogToJson(entries: QuestionLogEntry[]): string {
  return JSON.stringify(entries, null, 2);
}

export function downloadTextFile(content: string, filename: string, mime: string): void {
  // BOM so Excel opens UTF-8 CSV (names, £, accents) correctly.
  const blob = new Blob([mime.startsWith('text/csv') ? `\uFEFF${content}` : content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
